import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {Container, Row, Spinner} from "react-bootstrap";
import _ from "lodash";
import BoothMenuDiffAxiosService from "../../common/service/BoothMenuDiffAxiosService";
import {useNavigate, useOutletContext, useParams} from "react-router-dom";
import MessageView from "../../common/component/MessageView";
import RecipeUtils from "../../../../menu/common/jsonDynamic/util/RecipeUtils";
import DiffDataView from "../../../../menu/branchMenuDiff/dataView/DiffDataView";
import DataUtils from "../../../../menu/common/utils/DataUtils";
import OverlayToolTip from "../../../../menu/common/toolTip/OverlayToolTip";
import RecipeDataView from "../../../../menu/common/jsonDynamic/component/RecipeDataView";
import styled from "styled-components";
import DataCompareService from "../../../../../services/DataCompareService";
import {toast} from "react-toastify";
import AlertViewForBoothDiff from "../../common/component/AlertViewForBoothDiff";
import {FiLock, FiUnlock} from "react-icons/fi";
import AuthService from "../../../../../services/AuthService";
import ImsSystemRole from "../../../../../auth/roles/ImsSystemRole";
import ConfirmModal from "../../../../common/confirm/ConfirmModal";
import Sider from "../../../../common/sider/Sider";
import BoothMenuRecipeHistoryView from "./recipeHistory/BoothMenuRecipeHistoryView";

const SimpleButton = styled.button`
    font-size: 14px;
    padding: 7px 10px;
    color: ${(props) => props.enabled ? "#fc7242" : "#c2c2c2"};
    border: 1px solid ${(props) => props.enabled ? "#fc7242" : "#c2c2c2"};
    transition: all ease-in-out 0.2s;
    border-radius: 10px;
    background-color: white;

    &:focus {
        outline: none;
    }

    &:hover {
        background-color: ${(props) => props.enabled ? "#ffeee6" : "white"};
    }
`;

const SimpleGreyButton = styled.button`
    font-size: 14px;
    padding: 7px 10px;
    color: ${(props) => props.enabled ? "#757575" : "#c2c2c2"};
    border: 1px solid ${(props) => props.enabled ? "#757575" : "#c2c2c2"};
    transition: all ease-in-out 0.2s;
    border-radius: 10px;
    background-color: white;

    &:focus {
        outline: none;
    }

    &:hover {
        background-color: ${(props) => props.enabled ? "#ebebeb" : "white"};
    }
`;

const TransButton = styled.button`
    border: none;
    background: none;
    text-align: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all ease-in-out 0.2s;
    color: #757575;

    &:hover {
        color: ${(props) => props.enabled ? "black" : ""};
    }

    &:focus {
        outline: none;
    }
`;

const MenuRecipeDiffView = ({boothVersionId}) => {

    const {t} = useTranslation();
    const {boothId, menuId} = useParams();
    const {fetchBoothMenuList, boothInfo} = useOutletContext();
    const navigate = useNavigate();

    /* states */
    const [defaultDataEntity, setDefaultDataEntity] = useState({});
    /** menu detail entity before merge diff */
    const [productSchema, setProductSchema] = useState({});

    const [recipeHistoryOpen, setRecipeHistoryOpen] = useState(false);

    /* init-targets */
    const [diffEntity, setDiffEntity] = useState({});
    const [fixedDiffEntity, setFixedDiffEntity] = useState({});
    const [isValidMap, setIsValidMap] = useState({});
    const [isValid, setIsValid] = useState(true);
    const [isEdited, setIsEdited] = useState(false);
    const [isRecipeDiffValid, setIsRecipeDiffValid] = useState(false);
    const [isRecipeDiffUsed, setIsRecipeDiffUsed] = useState(false);

    /* loading */
    const [isLoading, setIsLoading] = useState(false);
    const [isSaveLoading, setIsSaveLoading] = useState(false);

    /* confirmation for the lock */
    const [isLockReleaseConfirm, setIsLockReleaseConfirm] = useState(false);
    const [isLockConfirm, setIsLockConfirm] = useState(false);

    /* fetch product schema */
    const getProductSchema = async (newMenuInfo, productSchemaVersion, newDetailEntity) => {
        const onSuccessCallback = async (data) => {
            await setProductSchema(data['productSchema']);
            await initializeStates(newMenuInfo, data['productSchema'], newDetailEntity);
            await setIsLoading(false);
        };
        const onFailureCallback = async (err) => {
            await setProductSchema({});
            await initializeStates(newMenuInfo, {}, newDetailEntity);
            await setIsLoading(false);
            console.log(err);
        };
        await BoothMenuDiffAxiosService.fetchProductSchema(boothVersionId, productSchemaVersion, onSuccessCallback, onFailureCallback);
    };

    const getBoothMenuDiff = async (callback) => {
        await setIsLoading(true);
        let isFound = false;
        const onSuccessCallback = async (data) => {
            let newDetailEntity = {};
            let newMenuInfo = {};
            /* extract the target detail */
            let boothMenuList = data['boothMenuList'];
            boothMenuList.map(boothMenuInfo => {
                if (boothMenuInfo['menuInfo']['franchiseMenuId'] === menuId) {
                    let detailList = boothMenuInfo['menuDetailListBeforeMergeDiff'];
                    detailList.map(detailEntity => {
                        if (detailEntity['franchiseMenuDetailBoothVersionId'] === boothVersionId) {
                            newMenuInfo = _.cloneDeep(boothMenuInfo);
                            newDetailEntity = _.cloneDeep(detailEntity);
                            isFound = true;
                        }
                    });
                }
            });
            if (isFound) {
                await setDefaultDataEntity(newDetailEntity);
                await callback(newMenuInfo, newDetailEntity['franchiseMenuDetailProductJsonVersion'], newDetailEntity);
            } else {
                await onFailureCallback({"message": "Cannot find the target menu detail."});
            }
        };
        const onFailureCallback = async (err) => {
            navigate('/not-found');
            console.log(err);
            await setDefaultDataEntity({});
            await setProductSchema({});
            await initializeStates({}, {}, {});
            await setIsLoading(false);
        };
        await BoothMenuDiffAxiosService.fetchBoothMenuList(boothId, onSuccessCallback, onFailureCallback);
    };

    /* init */
    /** when params are an empty object, error-init. */
    const initializeStates = async (newMenuInfo, jsonSchema, defaultDetailInfo) => {
        /* error-init */
        if ((_.isEqual(newMenuInfo, {})) || (_.isEqual(jsonSchema, {}))) {
            await setDiffEntity({});
            await setFixedDiffEntity({});
            await setIsValidMap({});
            await setIsValid(true);
            await setIsEdited(false);
            await setIsRecipeDiffValid(false);
            await setIsRecipeDiffUsed(false);
            return;
        }
        /* extract the diff entity */
        let newDiffEntity = {};
        let isDiffExist = false;
        newMenuInfo['menuDetailDiffList'].map(menuDetailDiffInfo => {
            if (menuDetailDiffInfo['boothMenuDetailDiffBoothId'] === boothId) {
                newDiffEntity = menuDetailDiffInfo;
                isDiffExist = true;
            }
        });
        /* craft a newRequestBody, isValidMap */
        let newIsValidMap = {};
        let newRequestBody = {};
        let isRecipeDiffValidTemp = true;
        let isRecipeDiffExist = false;
        /* isValidMap */
        newIsValidMap['isEnabled'] = true;
        Object.keys(jsonSchema['spec']['attributes']).map(fieldKey => {
            newIsValidMap[fieldKey] = true;
        });
        if (isDiffExist) {
            /* isEnabled */
            newRequestBody['isEnabled'] = (newDiffEntity['isEnabled'] != null) ? newDiffEntity['isEnabled'] : null;
            /* recipe validation */
            if (newDiffEntity['boothMenuDetailDiffRecipe'] != null) {
                /* check diff validity */
                isRecipeDiffValidTemp = RecipeUtils.validateJsonDataWithSchema(jsonSchema, newDiffEntity['boothMenuDetailDiffRecipe']);
                isRecipeDiffExist = true;
            }
        } else {
            /* isEnabled */
            newRequestBody['isEnabled'] = null;
        }
        await setIsRecipeDiffValid(isRecipeDiffValidTemp);
        /* recipe initialization */
        let recipeDiff = null;
        if (isRecipeDiffExist) {
            if (isRecipeDiffValidTemp) {
                recipeDiff = JSON.parse(newDiffEntity['boothMenuDetailDiffRecipe'])['attributes'];
                Object.keys(jsonSchema['spec']['attributes']).map(fieldKey => {
                    newRequestBody[fieldKey] = isRecipeDiffExist ? recipeDiff[fieldKey] : null;
                });
            }
            await setIsRecipeDiffUsed(true);
        } else {
            Object.keys(jsonSchema['spec']['attributes']).map(fieldKey => {
                newRequestBody[fieldKey] = null;
            });
            await setIsRecipeDiffUsed(false);
        }
        await setDiffEntity(newRequestBody);
        await setFixedDiffEntity(_.cloneDeep(newRequestBody));
        await setIsValidMap(newIsValidMap);
        await setIsValid(true);
        await setIsEdited(false);
    };

    const formatRequestBody = (prevRequestBody) => {
        let newRequestBody = {};
        let prevRequestBodyCopy = _.cloneDeep(prevRequestBody);

        newRequestBody['isEnabled'] = prevRequestBodyCopy['isEnabled'];
        delete prevRequestBodyCopy['isEnabled'];

        if (isRecipeDiffUsed) {
            let diffRecipe = {
                "attributes": prevRequestBodyCopy
            };
            newRequestBody['franchiseMenuDetailRecipe'] = JSON.stringify(diffRecipe);
        } else {
            newRequestBody['franchiseMenuDetailRecipe'] = null;
        }
        return newRequestBody;
    };

    const putMenuDetailDiff = async () => {
        await setIsSaveLoading(true);
        let requestBody = formatRequestBody(diffEntity);
        const onSuccessCallback = async () => {
            toast.success(<AlertViewForBoothDiff message={t("message.saved")}/>);
            await getBoothMenuDiff(getProductSchema);
            await setIsSaveLoading(false);
        };
        const onFailureCallback = async (err) => {
            toast.error(<AlertViewForBoothDiff
                message={t("menu.franchiseMenu.recipeInfo.menuRecipe.message.error.saveError")}/>);
            await setIsSaveLoading(false);
            console.log(err);
        };
        await BoothMenuDiffAxiosService.putBoothMenuDiff(boothId, menuId, defaultDataEntity['franchiseMenuDetailId'], requestBody, onSuccessCallback, onFailureCallback);
    };

    const makeRecipeDiffNull = async () => {
        let newIsValidMap = {};
        newIsValidMap['isEnabled'] = true;
        Object.keys(productSchema['spec']['attributes']).map(fieldKey => {
            newIsValidMap[fieldKey] = true;
        });
        await setIsValidMap(newIsValidMap);
        await setDiffEntity(prevEntity => {
            let newEntity = {...prevEntity};
            Object.keys(productSchema['spec']['attributes']).map(fieldKey => {
                newEntity[fieldKey] = null;
            });
            return newEntity;
        });
    };

    const makeRecipeDiffFull = async () => {
        let newIsValidMap = {};
        newIsValidMap['isEnabled'] = true;
        Object.keys(productSchema['spec']['attributes']).map(fieldKey => {
            newIsValidMap[fieldKey] = true;
        });
        await setIsValidMap(newIsValidMap);
        await setDiffEntity(prevEntity => {
            let newEntity = {...prevEntity};
            Object.keys(productSchema['spec']['attributes']).map(fieldKey => {
                newEntity[fieldKey] = defaultDataEntity['franchiseMenuDetailRecipe']['attributes'][fieldKey];
            });
            return newEntity;
        });
    };

    /* lock methods */
    const toggleLock = async () => {
        if (isRecipeDiffUsed) {
            await makeRecipeDiffNull();
            await setIsRecipeDiffUsed(false);
        } else {
            await makeRecipeDiffFull();
            await setIsRecipeDiffUsed(true);
        }
    };

    /* effects */
    useEffect(() => {
        getBoothMenuDiff(getProductSchema);
    }, [menuId, boothVersionId]);

    useEffect(() => {
        setIsEdited(!_.isEqual(diffEntity, fixedDiffEntity));
    }, [diffEntity]);

    useEffect(() => {
        setIsValid(DataCompareService.checkIsAllTrue(isValidMap));
    }, [isValidMap]);

    if (isLoading) {
        /* loading */
        return (
            <div
                style={{
                    width: '100%',
                    padding: '30px 0px',
                    textAlign: 'center'
                }}
            >
                <Spinner size={"sm"}/>
            </div>
        );
    } else if ((!isLoading) && _.isEqual(defaultDataEntity, {})) {
        /* the detail does not exist : MUST NOT BE REACHED */
        return (
            <div
                style={{
                    width: '100%',
                    padding: '30px 0px',
                    textAlign: 'center'
                }}
            >
                <MessageView
                    message={t("menu.boothMenuDiff.messages.error.detailNotFound")}
                />
            </div>
        );
    } else if ((!isLoading) && (_.isEqual(productSchema, {}) || _.isEqual(defaultDataEntity, {}))) {
        /* the option schema does not exist : MUST NOT BE REACHED */
        return (
            <div
                style={{
                    width: '100%',
                    padding: '30px 0px',
                    textAlign: 'center'
                }}
            >
                <MessageView
                    message={t("menu.boothMenuDiff.messages.error.schemaNotFound")}
                />
            </div>
        );
    } else if ((!isLoading)
        && (!_.isEqual(productSchema, {}))
        && (!_.isEqual(defaultDataEntity, {}))
        && (!RecipeUtils.validateJsonDataWithSchema(productSchema, JSON.stringify(defaultDataEntity['franchiseMenuDetailRecipe'])))) {
        /* the default data (before merge) validation fail */
        return (
            <div
                style={{
                    width: '100%',
                    padding: '30px 0px',
                    textAlign: 'center'
                }}
            >
                <MessageView
                    message={t("menu.boothMenuDiff.messages.error.schemaNotfound")}
                />
            </div>
        );
    } else {
        return (
            <Container fluid>
                <Row>
                    <DiffDataView
                        type={"switch"}
                        hoverMessageForUseDiffButton={t("menu.boothMenuDiff.messages.diff.hoverMessage", {fieldName: t("menu.boothMenuDiff.field.isEnabled")})}
                        title={t("menu.boothMenuDiff.field.isEnabled")}
                        originalDataKey={"isEnabled"}
                        diffDataKey={"isEnabled"}
                        diffEntity={diffEntity}
                        originalData={defaultDataEntity}
                        isValidMap={isValidMap}
                        updateDiffEntity={(key, value) => DataUtils.updateHelper(setDiffEntity, key, value)}
                        updateIsValidMap={(key, value) => DataUtils.updateHelper(setIsValidMap, key, value)}
                        isForEdit={AuthService.hasAuthByRoleNameAndType(ImsSystemRole.BOOTH_MANAGEMENT, "PUT")}
                        booleanReversed={true}
                        booleanOnMessage={t("message.menuOnThisBooth")}
                        booleanOffMessage={t("message.menuOffThisBooth")}
                    />
                </Row>
                {/* recipe dynamic */}
                {/* validate the recipe with the schema needed */}
                <Row>
                    <div
                        style={{
                            display: 'inline-flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: "flex-start",
                            paddingLeft: '10px',
                            marginBottom: '5px'
                        }}
                    >
                        <span
                            style={{
                                color: '#8f8f8f',
                                fontSize: '14px'
                            }}
                        >
                            {t("menu.boothMenuDiff.field.menuRecipe")}
                        </span>
                        <div
                            style={{
                                height: '100%',
                                display: 'inline-block',
                                marginLeft: "10px"
                            }}
                        >
                            {
                                (isRecipeDiffUsed) &&
                                <OverlayToolTip
                                    direction={"top"}
                                    text={t("menu.boothMenuDiff.messages.diff.hoverMessage", {fieldName: t("menu.boothMenuDiff.field.menuRecipe")})}
                                >
                                    <TransButton
                                        onClick={async () => setIsLockConfirm(true)}
                                    >
                                        <FiUnlock
                                            size={"14"}
                                            color={"#fc7242"}
                                        />
                                    </TransButton>
                                </OverlayToolTip>
                            }
                            {
                                (!isRecipeDiffUsed) &&
                                <TransButton
                                    onClick={() => setIsLockReleaseConfirm(true)}
                                    enabled={true}
                                >
                                    <FiLock
                                        size={"14"}
                                    />
                                </TransButton>
                            }
                            <ConfirmModal
                                isOpen={isLockReleaseConfirm}
                                setIsOpen={setIsLockReleaseConfirm}
                                title={t("menu.boothMenuDiff.messages.lockConfirm.release")}
                                confirmLabel={t("button.yes")}
                                rejectLabel={t("button.no")}
                                onConfirm={toggleLock}
                                onReject={() => {
                                }}
                            />
                            <ConfirmModal
                                isOpen={isLockConfirm}
                                setIsOpen={setIsLockConfirm}
                                title={t("menu.boothMenuDiff.messages.lockConfirm.lock")}
                                confirmLabel={t("button.yes")}
                                rejectLabel={t("button.no")}
                                onConfirm={toggleLock}
                                onReject={() => {
                                }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            width: '100%',
                            height: '485px',
                            overflow: 'auto',
                            border: '1px solid #ebebeb',
                            borderRadius: '10px',
                            padding: '10px',
                            backgroundColor: isRecipeDiffUsed ? "white" : "#F8F8F8",
                            transition: 'all ease-in-out 0.2s'
                        }}
                    >
                        {/* recipe view */}
                        {
                            (!isRecipeDiffUsed) &&
                            Object.keys(productSchema['spec']['attributes']).map(fieldKey => {
                                return (
                                    <RecipeDataView
                                        title={productSchema['spec']['attributes'][fieldKey][t("language.dataKey.boothControl.json.label")]}
                                        fieldKey={fieldKey}
                                        validator={productSchema['spec']['attributes'][fieldKey]['validator']}
                                        isCategorical={productSchema['spec']['attributes'][fieldKey]['isCategorical']}
                                        data={defaultDataEntity['franchiseMenuDetailRecipe']['attributes'][fieldKey]}
                                        updateData={() => {
                                        }}
                                        isValidMap={isValidMap}
                                        updateIsValidMap={() => {
                                        }}
                                        isForEdit={false}
                                        unitOfValue={productSchema['spec']['attributes'][fieldKey]['unitOfValue']}
                                    />
                                );
                            })
                        }
                        {
                            isRecipeDiffUsed && isRecipeDiffValid &&
                            Object.keys(productSchema['spec']['attributes']).map(fieldKey => {
                                return (
                                    <RecipeDataView
                                        title={productSchema['spec']['attributes'][fieldKey][t("language.dataKey.boothControl.json.label")]}
                                        fieldKey={fieldKey}
                                        validator={productSchema['spec']['attributes'][fieldKey]['validator']}
                                        isCategorical={productSchema['spec']['attributes'][fieldKey]['isCategorical']}
                                        data={diffEntity[fieldKey]}
                                        updateData={(value) => DataUtils.updateHelper(setDiffEntity, fieldKey, value)}
                                        isValidMap={isValidMap}
                                        updateIsValidMap={(value) => DataUtils.updateHelper(setIsValidMap, fieldKey, value)}
                                        isForEdit={AuthService.hasAuthByRoleNameAndType(ImsSystemRole.BOOTH_MANAGEMENT, "PUT")}
                                        unitOfValue={productSchema['spec']['attributes'][fieldKey]['unitOfValue']}
                                    />
                                );
                            })
                        }
                        {
                            isRecipeDiffUsed && (!isRecipeDiffValid) &&
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%'
                                }}
                            >
                                <MessageView
                                    message={t("menu.boothMenuDiff.messages.error.validationFail")}
                                />
                                <SimpleButton
                                    enabled={true}
                                    onClick={async () => {
                                        await makeRecipeDiffFull();
                                        await setIsRecipeDiffValid(true);
                                    }}
                                >
                                    {t("menu.boothMenuDiff.button.initDiff")}
                                </SimpleButton>
                            </div>
                        }
                    </div>
                </Row>
                <Row>
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            marginTop: '10px'
                        }}
                    >
                        {/* init button */}
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center'
                            }}
                        >
                            {/* initialize button */}
                            <SimpleGreyButton
                                enabled
                                onClick={() => {
                                    setRecipeHistoryOpen(true);
                                }}
                            >
                                {t("boothRecipeHistory.title")}
                            </SimpleGreyButton>
                            <Sider
                                open={recipeHistoryOpen}
                                setOpen={setRecipeHistoryOpen}
                            >
                                <BoothMenuRecipeHistoryView
                                    boothId={boothId}
                                    menuId={menuId}
                                    detailId={defaultDataEntity['franchiseMenuDetailId']}
                                />
                            </Sider>
                        </div>
                        {/* init button */}
                        <div
                            style={{
                                marginLeft: 'auto',
                                display: 'inline-flex',
                                alignItems: 'center'
                            }}
                        >
                            {/* initialize button */}
                            <SimpleGreyButton
                                enabled={isEdited}
                                onClick={() => {
                                    if (isEdited) {
                                        getBoothMenuDiff(getProductSchema);
                                    }
                                }}
                            >
                                {t("menu.franchiseMenu.recipeInfo.menuRecipe.button.init")}
                            </SimpleGreyButton>
                        </div>
                        {/* save button */}
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                marginLeft: '10px'
                            }}>
                            {/* save button */}
                            <SimpleButton
                                enabled={isEdited && isValid && !isSaveLoading}
                                onClick={() => {
                                    if (isEdited && isValid && !isSaveLoading) {
                                        putMenuDetailDiff();
                                    }
                                }}
                            >
                                {
                                    isSaveLoading ?
                                        <Spinner size={"sm"}/>
                                        :
                                        <>
                                            {t("button.save")}
                                        </>
                                }
                            </SimpleButton>
                        </div>
                    </div>
                </Row>
            </Container>
        );
    }
};

export default MenuRecipeDiffView;
